/**
 * QRSidebarWidget Component
 * Collapsible QR code widget for dashboard sidebar
 */

import React, { useState, useEffect, useRef } from 'react';
import { QrCode, Download, Copy, Check, X } from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import './QRSidebarWidget.css';

function QRSidebarWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Fetch QR code on first open
  useEffect(() => {
    if (isOpen && !qrData) {
      fetchStoreQR();
    }
  }, [isOpen]);

  // Close on click outside or Esc
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const fetchStoreQR = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/qrcode/store', {
        params: { size: 180 }
      });
      if (response.data.success) {
        setQrData(response.data);
      }
    } catch (err) {
      console.error('Error fetching store QR:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axiosInstance.get('/qrcode/download/store', {
        params: { size: 500 },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'store-qr-code.png');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading QR:', err);
    }
  };

  const handleCopyUrl = async () => {
    if (!qrData?.url) return;

    try {
      await navigator.clipboard.writeText(qrData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying URL:', err);
    }
  };

  return (
    <>
      {/* Topbar trigger — matches .header-action-btn shape */}
      <button
        ref={triggerRef}
        type="button"
        className={`qr-widget-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Store QR code"
        aria-expanded={isOpen}
        title="Store QR code"
      >
        <QrCode size={30} strokeWidth={2.25} />
      </button>

      {/* Dropdown popover anchored under the topbar */}
      <div ref={panelRef} className={`qr-widget-panel ${isOpen ? 'open' : ''}`}>
        <div className="qr-widget-header">
          <h4>
            <QrCode size={18} />
            Store QR Code
          </h4>
          <button className="qr-widget-close" onClick={() => setIsOpen(false)}>
            <X size={16} />
          </button>
        </div>

        <div className="qr-widget-content">
          {loading ? (
            <div className="qr-widget-loading">
              <div className="qr-widget-spinner" />
              <span>Loading...</span>
            </div>
          ) : qrData ? (
            <>
              <div className="qr-widget-image">
                <img src={qrData.qrCode} alt="Store QR Code" />
              </div>

              <p className="qr-widget-description">
                Scan to visit your store
              </p>

              <div className="qr-widget-url">
                <span>{qrData.url}</span>
              </div>

              <div className="qr-widget-actions">
                <button onClick={handleDownload} title="Download">
                  <Download size={16} />
                  Download
                </button>
                <button onClick={handleCopyUrl} title="Copy URL">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </>
          ) : (
            <div className="qr-widget-error">
              <span>Failed to load QR code</span>
              <button onClick={fetchStoreQR}>Retry</button>
            </div>
          )}
        </div>

        <div className="qr-widget-footer">
          <small>Share this QR code with customers</small>
        </div>
      </div>
    </>
  );
}

export default QRSidebarWidget;
